import { useState } from 'react';
import type { ReactNode } from 'react';
import { DATE_FORMATS, formatDateTime } from '@belong/common';
import { IconCaretDown, IconCaretUp, IconCheck } from '@belong/icons';
import { ButtonBase, Image, Text } from '@belong/ui';
import type {
  ImprovementsProgressItemModel,
  SpaceListingModel,
} from 'api/models';
import Label from 'components/Label/Label';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import {
  getDisplayableMedia,
  getRoomLabels,
  getStatusBadge,
  getTradeLabel,
} from '../../utils';
import { ProgressItemMedia } from '../progress-item-media/progress-item-media';
import { RepairPlaceholder } from '../repair-placeholder/repair-placeholder';

type Props = {
  item: ImprovementsProgressItemModel;
  spaces: SpaceListingModel[];
};

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-xs">
      <IconCheck width={16} className="mt-[2px] shrink-0 text-green" />
      <Text variant="p1">
        <Text
          as="span"
          variant="p1"
          fontWeight="semibold"
          className="text-navy"
        >
          {label}
        </Text>{' '}
        {value}
      </Text>
    </div>
  );
}

export function ProgressItemCard({ item, spaces }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const panelId = `progress-item-${item.uniqueId}`;

  const roomLabels = getRoomLabels(item.owners, spaces);
  const badge = getStatusBadge(item.status);
  const thumbnailUrl = getDisplayableMedia(item.media)[0]?.url;
  const appointmentDetail = item.appointmentDate
    ? [
        item.summary,
        formatDateTime({
          dateTime: item.appointmentDate,
          format: DATE_FORMATS.MONTH_DAY_SHORT,
        }),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  const tradeLabel = getTradeLabel(item.trade);

  return (
    <div className="rounded border border-solid border-gray p-sm">
      <ButtonBase
        className="flex w-full flex-wrap items-center gap-x-sm gap-y-xs text-left"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <div className="h-[60px] w-[60px] shrink-0 overflow-hidden rounded-small border border-solid border-gray">
          {thumbnailUrl ? (
            <Image alt="" src={thumbnailUrl} className="object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <RepairPlaceholder size={40} />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <Text fontWeight="semibold" className="text-navy">
            {item.summary}
          </Text>
          <Text variant="p1" className="text-dark-gray">
            {tradeLabel}
          </Text>
          {roomLabels.length > 0 && (
            <Text variant="p1" className="text-gray">
              {roomLabels.join(', ')}
            </Text>
          )}
        </div>

        <div className="order-3 shrink-0 text-navy">
          {isExpanded ? (
            <IconCaretUp width={14} height={14} />
          ) : (
            <IconCaretDown width={14} height={14} />
          )}
        </div>

        {badge && (
          <div className="order-4 w-full shrink-0 md:order-2 md:w-auto">
            <Label color={badge.color} text={badge.label} />
          </div>
        )}
      </ButtonBase>

      {isExpanded && (
        <div
          id={panelId}
          className="mt-sm border-0 border-t border-dashed border-gray pt-sm"
        >
          {item.description && (
            <Text className="mb-sm">{item.description}</Text>
          )}

          <div className="flex flex-col gap-xs">
            {item.completedOn && (
              <DetailRow
                label={POST_INSPECTION_FLOW_STRINGS['progress.item.completed']}
                value={formatDateTime({
                  dateTime: item.completedOn,
                  format: DATE_FORMATS.FULL_MONTH_DAY_YEAR,
                })}
              />
            )}

            {appointmentDetail && (
              <DetailRow
                label={
                  POST_INSPECTION_FLOW_STRINGS['progress.item.appointment']
                }
                value={appointmentDetail}
              />
            )}
          </div>

          <ProgressItemMedia media={item.media} />
        </div>
      )}
    </div>
  );
}
